import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Container } from '@mui/material';
import { useSupabase } from '../contexts/SupabaseContext';

const AuthCallback: React.FC = () => {
  const { supabase } = useSupabase();
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Log the current URL for debugging
        console.log('Current URL:', window.location.href);
        console.log('Hash:', window.location.hash);
        console.log('Search:', window.location.search);
        
        // Get the current URL with hash and search params
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const searchParams = new URLSearchParams(window.location.search);
        
        // Log all parameters for debugging
        console.log('Hash params:', Object.fromEntries(hashParams.entries()));
        console.log('Search params:', Object.fromEntries(searchParams.entries()));
        
        // Check if we have OAuth parameters
        const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token');
        const error = hashParams.get('error') || searchParams.get('error');
        const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');
        
        if (error) {
          console.error('OAuth error:', error);
          console.error('Error description:', errorDescription);
          navigate('/login?error=auth_failed');
          return;
        }
        
        if (accessToken && refreshToken) {
          console.log('Found tokens, setting session...');
          // Set the session manually
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          
          if (sessionError) {
            console.error('Session error:', sessionError);
            navigate('/login?error=session_failed');
          } else {
            console.log('Authentication successful:', data.session?.user);
            navigate('/');
          }
        } else {
          console.log('No tokens found, trying getSession...');
          // Try to get existing session
          const { data, error } = await supabase.auth.getSession();
          if (error) {
            console.error('Auth callback error:', error);
            navigate('/login?error=auth_failed');
          } else if (data.session) {
            console.log('Found existing session:', data.session.user);
            navigate('/');
          } else {
            console.log('No session found, redirecting to login');
            navigate('/login');
          }
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        navigate('/login?error=unexpected');
      }
    };

    handleAuthCallback();
  }, [supabase.auth, navigate]);

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" gutterBottom>
            Completing sign in...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we verify your authentication.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default AuthCallback;